import React, { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Alert,
  Chip
} from '@mui/material';
import {
  Download as DownloadIcon,
  FileDownload as FileDownloadIcon,
  Share as ShareIcon
} from '@mui/icons-material';
import { businessFinder } from '../utils/businessFinder';

const ExportResults = ({ results, open, onClose }) => {
  const [exportFormat, setExportFormat] = useState('csv');
  const [isExporting, setIsExporting] = useState(false);
  const [exportSuccess, setExportSuccess] = useState(false);

  const handleExport = async () => {
    if (!results || results.length === 0) {
      return;
    }

    setIsExporting(true);
    setExportSuccess(false);

    try {
      const exportData = businessFinder.exportResults(results, exportFormat);
      
      // Création du fichier à télécharger
      const blob = new Blob([exportData], {
        type: exportFormat === 'csv' ? 'text/csv' : 'application/json'
      });
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `prospection-${new Date().toISOString().split('T')[0]}.${exportFormat}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      setExportSuccess(true);
    } catch (error) {
      console.error('Erreur lors de l\'export:', error);
    } finally {
      setIsExporting(false);
    }
  };

  const handleClose = () => {
    setExportSuccess(false);
    onClose();
  };

  const getExportSummary = () => {
    if (!results) return null;
    
    const highPriority = results.filter(r => r.businessPotential === 'high').length;
    const mediumPriority = results.filter(r => r.businessPotential === 'medium').length;
    const lowPriority = results.filter(r => r.businessPotential === 'low').length;
    
    return {
      total: results.length,
      highPriority,
      mediumPriority,
      lowPriority
    };
  };

  const summary = getExportSummary();

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FileDownloadIcon />
          Exporter les résultats
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {exportSuccess && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Export réussi ! Le fichier a été téléchargé.
          </Alert>
        )}
        
        <Typography variant="body1" gutterBottom>
          Exportez vos résultats de prospection pour les utiliser dans votre CRM ou tableur.
        </Typography>
        
        {summary && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Résumé de la prospection
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip label={`Total: ${summary.total}`} color="primary" />
              <Chip label={`Priorité haute: ${summary.highPriority}`} color="error" />
              <Chip label={`Priorité moyenne: ${summary.mediumPriority}`} color="warning" />
              <Chip label={`Priorité basse: ${summary.lowPriority}`} color="success" />
            </Box>
          </Box>
        )}
        
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Format d'export</InputLabel>
          <Select
            value={exportFormat}
            label="Format d'export"
            onChange={(e) => setExportFormat(e.target.value)}
          >
            <MenuItem value="csv">CSV (Excel, Google Sheets)</MenuItem>
            <MenuItem value="json">JSON (Données structurées)</MenuItem>
          </Select>
        </FormControl>
        
        <Typography variant="body2" color="text.secondary">
          <strong>CSV :</strong> Compatible avec Excel, Google Sheets et la plupart des CRM
          <br />
          <strong>JSON :</strong> Format structuré pour l'intégration avec d'autres outils
        </Typography>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={handleClose} disabled={isExporting}>
          Annuler
        </Button>
        <Button
          onClick={handleExport}
          variant="contained"
          startIcon={<DownloadIcon />}
          disabled={isExporting || !results || results.length === 0}
        >
          {isExporting ? 'Export en cours...' : 'Exporter'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ExportResults;